/**
 * useSync - manages the WebSocket connection for real-time sync.
 *
 * Lifecycle:
 *  1. Connects after login (when JWT is available in auth store).
 *  2. JWT is passed as ?token= query parameter during WebSocket upgrade
 *     (browser WebSocket API cannot set custom headers).
 *  3. CalendarRoom DO validates JWT on HTTP upgrade, session is pre-authenticated.
 *  4. On connect, sends subscribe with lastSync timestamp immediately.
 *  5. Handles sync (full delta) and change (single entity) messages.
 *  6. Responds to server pings with pong.
 *  7. Flushes offline sync queue on reconnect (FIFO order, with token refresh).
 *  8. Disconnects on logout.
 *
 * WebSocket is managed by React, NOT the service worker, per architecture.md.
 */

import { useEffect, useRef } from 'react';
import type { Event, Task, Note } from '@anna-calendar/shared';
import type { ChangeEntry } from '@anna-calendar/shared';
import { createReconnectingWebSocket, type ReconnectHandle } from '@anna-calendar/shared';
import { useAuthStore } from '../store/auth';
import { useSyncStore } from '../store/sync';
import { useEventsStore } from '../store/events';
import { useTasksStore } from '../store/tasks';
import { useNotesStore } from '../store/notes';
import { useAppStore } from '../store/app';
import { getApiBase } from '../api/client';
import { getQueuedMutations, dequeueMutation } from '../db/indexeddb';

/** Apply a single change entry from sync or change message to the right store. */
function applyChange(entry: ChangeEntry): void {
  const { entity, action, data } = entry;
  switch (entity) {
    case 'event':
      useEventsStore.getState().applyRemoteChange(action, data as Event | { id: string });
      break;
    case 'task':
      useTasksStore.getState().applyRemoteChange(action, data as Task | { id: string });
      break;
    case 'note':
      useNotesStore.getState().applyRemoteChange(action, data as Note | { id: string });
      break;
  }
}

/**
 * Replay queued offline mutations against the API, one at a time in FIFO order.
 *
 * If a 401 is received during replay, attempts to refresh the JWT token and
 * retries the current mutation. If refresh fails, stops replay (remaining items
 * stay queued for the next reconnect attempt).
 */
async function flushSyncQueue(getToken: () => string | null): Promise<void> {
  const queued = await getQueuedMutations();
  if (queued.length === 0) return;

  const apiBase = getApiBase();
  let successCount = 0;
  let token = getToken();
  if (!token) return;

  for (const entry of queued) {
    try {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
      };
      const init: RequestInit = { method: entry.method };
      if (entry.body) {
        headers['Content-Type'] = 'application/json';
        init.body = entry.body;
      }
      init.headers = headers;

      let res = await fetch(`${apiBase}${entry.path}`, init);

      // Handle token expiry during queue replay with a single refresh attempt
      if (res.status === 401) {
        const authState = useAuthStore.getState();
        const refreshed = await authState.tryRefresh();
        if (refreshed) {
          token = authState.token;
          if (!token) break;
          // Retry with fresh token
          headers['Authorization'] = `Bearer ${token}`;
          init.headers = headers;
          res = await fetch(`${apiBase}${entry.path}`, init);
        } else {
          // Refresh failed - stop replay, items stay queued
          console.warn('[sync] Token refresh failed during queue replay, stopping');
          break;
        }
      }

      if (res.ok || res.status === 404) {
        // 404 means entity was already deleted elsewhere - safe to dequeue
        await dequeueMutation(entry.id);
        successCount++;
      } else if (res.status === 401) {
        // Still 401 after refresh attempt - stop
        break;
      } else {
        // Server rejected (400, 409, etc.) - discard to avoid infinite loop
        console.warn(`[sync] Queue entry ${entry.id} rejected with ${res.status}, discarding`);
        await dequeueMutation(entry.id);
        successCount++;
      }
    } catch {
      // Network error during replay - stop, remaining items stay queued
      break;
    }
  }

  if (successCount > 0) {
    console.log(`[sync] Flushed ${successCount} queued mutations`);
  }
}

/**
 * Connect a WebSocket after login, disconnect on logout.
 * Manages the full sync protocol lifecycle.
 */
export function useSync(): void {
  const handleRef = useRef<ReconnectHandle | null>(null);
  const token = useAuthStore((s) => s.token);
  const isOnline = useAppStore((s) => s.isOnline);

  // Load persisted lastSync on mount
  useEffect(() => {
    useSyncStore.getState().loadLastSync();
  }, []);

  // Connect/disconnect based on auth token
  useEffect(() => {
    if (!token) {
      // Disconnect on logout
      if (handleRef.current) {
        handleRef.current.disconnect();
        handleRef.current = null;
        useSyncStore.getState().setStatus('disconnected');
      }
      return;
    }

    const { setStatus, setLastSync } = useSyncStore.getState();

    setStatus('connecting');

    const handle = createReconnectingWebSocket({
      connect: () => {
        const currentToken = useAuthStore.getState().token;
        if (!currentToken) throw new Error('No token available');
        const base = getApiBase().replace(/^http/, 'ws');
        // Browser WebSocket API cannot set custom headers, so pass JWT
        // as a query parameter. The CalendarRoom DO falls back to this
        // when no Authorization header is present.
        return new WebSocket(`${base}/api/sync?token=${encodeURIComponent(currentToken)}`);
      },

      onOpen: () => {
        // JWT was already validated during HTTP upgrade via the ?token= query
        // parameter. CalendarRoom sets authenticated: true on the session, so
        // we can send the subscribe message directly.
        setStatus('connected');
        const lastSync = useSyncStore.getState().lastSync;
        const ws = handle.ws;
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'subscribe', lastSync: lastSync ?? null }));
        }
        // Flush offline queue with live token reference for refresh support
        flushSyncQueue(() => useAuthStore.getState().token).then(() => {
          getQueuedMutations().then((q) => {
            useSyncStore.getState().setPendingCount(q.length);
          });
        });
      },

      onMessage: async (event: MessageEvent) => {
        let msg: unknown;
        try {
          msg = JSON.parse(event.data as string);
        } catch {
          return;
        }

        const data = msg as Record<string, unknown>;

        switch (data.type) {
          case 'sync': {
            // Full delta response from subscribe
            const changes = data.changes as ChangeEntry[] | undefined;
            if (Array.isArray(changes)) {
              for (const entry of changes) {
                applyChange(entry);
              }
              // Update lastSync to the latest timestamp
              if (changes.length > 0) {
                const latest = changes.reduce((max, c) =>
                  c.timestamp > max ? c.timestamp : max,
                  changes[0].timestamp,
                );
                setLastSync(latest);
              }
            }
            break;
          }

          case 'change': {
            // Single entity change broadcast
            const entry = data as unknown as ChangeEntry;
            applyChange(entry);
            if (entry.timestamp) {
              setLastSync(entry.timestamp);
            }
            break;
          }

          case 'ping': {
            // Respond to server heartbeat
            const ws = handle.ws;
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ type: 'pong' }));
            }
            break;
          }

          case 'error': {
            console.warn('[sync] Server error:', data.message);
            break;
          }
        }
      },

      onClose: (event: CloseEvent) => {
        if (event.code === 4001) {
          // Auth failure - don't reconnect, force re-login
          setStatus('disconnected');
          useAuthStore.getState().logout();
          return;
        }
        setStatus('reconnecting');
      },

      onError: () => {
        // onclose fires after onerror, reconnection handled there
      },

      onReconnecting: () => {
        setStatus('reconnecting');
      },
    });

    handleRef.current = handle;

    return () => {
      handle.disconnect();
      handleRef.current = null;
      setStatus('disconnected');
    };
  }, [token]);

  // Force reconnect when coming back online
  useEffect(() => {
    if (isOnline && token && handleRef.current) {
      // Reconnect with fresh lastSync
      handleRef.current.reconnectNow();
    }
  }, [isOnline, token]);
}
